import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Target, Shield, Cpu, Crosshair, ChartLineUp, Warning, Fingerprint as FingerprintIcon, Shuffle, Trophy, X, Check, Scales, ListChecks } from "@phosphor-icons/react";
import ThemeToggle from "@/components/ThemeToggle";

// Sprint 63 — landing rewritten around the current engine: field model built from
// commercial-optimizer consensus, correlated Monte Carlo + Field-Win ranking,
// calibration against real DK results. Competitors are described by category
// label only (Key Decision #8). No trademark-style feature names.
export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      <TopNav />
      <StickyPricingCta onCta={() => nav("/pricing")} />
      <Hero onCta={() => nav("/pricing")} />
      <HowItWorks />
      <Systems />
      <Proof />
      <DkNative />
      <Comparison />
      <Pricing onPick={() => nav("/pricing")} />
      <NewToDFS />
      <Footer />
    </div>
  );
}

function StickyPricingCta({ onCta }) {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 800);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div data-testid="sticky-pricing-cta"
      className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}>
      <button onClick={onCta}
        className="bg-[#39FF14] text-black px-5 py-3 font-bold uppercase tracking-widest text-xs shadow-[0_0_32px_-8px_#39FF14] hover:bg-[#2ee30d] hover:shadow-[0_0_48px_-4px_#39FF14] transition-all flex items-center gap-2">
        <span className="mono text-[9px] tracking-widest opacity-70">$99/week</span>
        <span>Start This Week</span>
      </button>
    </div>
  );
}

function TopNav() {
  return (
    <header className="sticky top-0 z-40 bg-[#0a0a0a]/90 backdrop-blur border-b border-zinc-900">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src="/brand/helmet_transparent.png" alt="WarPaint" className="w-9 h-9 object-contain" />
          <div>
            <div className="heading font-black tracking-tighter uppercase leading-none">WarPaint DFS</div>
            <div className="overline mt-0.5">Vegas-Priced DraftKings Optimizer</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-xs uppercase tracking-widest text-zinc-300">
          <a href="#how" className="hover:text-white">How It Works</a>
          <a href="#systems" className="hover:text-white">The Engine</a>
          <a href="#proof" className="hover:text-white">Proof</a>
          <a href="#comparison" className="hover:text-white">Compare</a>
          <a href="#pricing" className="hover:text-white">Pricing</a>
        </nav>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Link to="/login" className="text-xs uppercase tracking-widest text-zinc-300 hover:text-white px-3 py-2" data-testid="nav-login">Sign In</Link>
          <Link to="/pricing" className="text-xs uppercase tracking-widest bg-[#39FF14] text-black px-4 py-2 font-bold hover:bg-[#2ee30d]" data-testid="nav-start">Start This Week</Link>
        </div>
      </div>
    </header>
  );
}

function Hero({ onCta }) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 mm-grid-bg opacity-40" />
      <div className="max-w-7xl mx-auto px-6 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative">
        <div className="lg:col-span-7">
          <div className="overline mb-6 flex items-center gap-3 flex-wrap">
            <span>NFL DFS · Milly Maker · GPP · Showdown · Cash</span>
            <span data-testid="dk-optimized-badge" className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#39FF14]/10 border border-[#39FF14]/40 text-[#39FF14] mono text-[10px] tracking-widest">
              <span className="w-1.5 h-1.5 bg-[#39FF14] rounded-full" />
              BUILT FOR DRAFTKINGS
            </span>
          </div>
          <h1 className="heading text-5xl md:text-7xl font-black uppercase leading-[0.9] tracking-tighter mb-6" data-testid="hero-headline">
            The sharpest<br />projections in DFS.<br />
            <span className="text-[#39FF14]">Priced by Vegas, not by opinion.</span>
          </h1>
          <p className="text-lg text-zinc-300 leading-relaxed max-w-xl mb-4">
            Every WarPaint projection starts where the smartest money already is: <span className="text-white font-semibold">player prop
            markets from a sharp-book consensus, vig stripped, converted to DraftKings points</span> — refreshed all week
            and re-checked before every lock, Thursday through Monday.
          </p>
          <p className="text-base text-zinc-400 leading-relaxed max-w-xl mb-8">
            <span className="text-white font-semibold">Live ownership projections built the same way.</span> We read where the public
            is juicing the props and turn it into a real %-drafted forecast that sums like DraftKings' own — so you know
            the chalk before the field does. Then, and only then, we build around what the other optimizers are telling
            their users to play.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <button onClick={onCta} data-testid="hero-cta" className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#2ee30d] text-black font-black uppercase tracking-widest text-sm py-4 px-6 transition-transform hover:scale-[1.02]">
              Start This Week — $99 <ArrowRight size={16} weight="bold" />
            </button>
            <a href="#how" className="text-xs uppercase tracking-widest text-zinc-300 hover:text-white">See how it works ↓</a>
          </div>
          <div className="mt-10 flex items-center gap-8 text-xs mono text-zinc-500 flex-wrap">
            <div><span className="text-[#39FF14] font-bold text-base mono">Props</span> from a vig-stripped sharp consensus</div>
            <div><span className="text-[#39FF14] font-bold text-base mono">Live Own%</span> that sums like DK's real %-drafted</div>
            <div><span className="text-[#39FF14] font-bold text-base mono">Graded</span> against real DK results every week</div>
          </div>
        </div>
        <div className="lg:col-span-5 flex items-center justify-center">
          <img src="/brand/helmet_transparent.png" alt="WarPaint" className="w-full max-w-md mm-ai-glow" />
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { step: "01", title: "Pick any slate", body: "Every DraftKings slate is synced from the lobby — Thursday, Early Only, Afternoon Only, Sun-Mon, every Showdown. Vegas lines, prop-market projections, live ownership, injuries, depth charts and weather are already on every player." },
  { step: "02", title: "Trust the numbers", body: "Projections come from a vig-stripped sharp consensus of player props, converted to DK scoring. Ownership comes from the same markets — where the public is juicing a prop, our %-drafted forecast rises with it. Both are graded against real DK results weekly." },
  { step: "03", title: "Build for the contest you enter", body: "Milly Maker, 150-max GPP, 3-max, single entry, Showdown or cash: the rules scale continuously to your field size and entry count. The engine over-builds candidates, simulates them against a correlated field, and keeps the ones that pay on DK's actual payout curve." },
  { step: "04", title: "Add the field edge, then ship", body: "Optionally load the projection exports from the sites the crowd subscribes to and the Shadow Field steps around their builds. Export the DK-ready CSV; before any lock a red alert flags scratches and one click fixes every affected lineup." },
];

function HowItWorks() {
  return (
    <section id="how" className="border-t border-zinc-900 py-24 bg-[#080808]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-10">
          <div className="overline mb-3 text-[#39FF14]">How it works</div>
          <h2 className="heading text-4xl md:text-5xl font-black uppercase tracking-tighter mb-4">
            Sharp inputs first.<br /><span className="text-[#39FF14]">Then the field edge.</span>
          </h2>
          <p className="text-lg text-zinc-300 max-w-2xl">
            An optimizer is only as good as the numbers it eats. WarPaint's projections and ownership are priced off
            the same prop markets the sharpest bettors move — then the engine builds for the exact contest you
            enter and, if you want it, around what the commercial optimizers are handing their users.
          </p>
        </div>
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-[#39FF14]/10 via-transparent to-[#E11D2E]/10 blur-3xl pointer-events-none" />
          <div className="relative border-2 border-zinc-800 bg-[#0a0a0a] p-8 mm-ai-glow">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {STEPS.map((s) => (
                <div key={s.step} className="bg-[#121212] border border-zinc-800 hover:border-[#39FF14]/40 transition-colors p-4">
                  <div className="mono text-[10px] tracking-widest text-[#39FF14] mb-1">STEP · {s.step}</div>
                  <div className="heading font-bold text-white mb-2">{s.title}</div>
                  <p className="text-xs text-zinc-300 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-zinc-900 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="inline-block w-1.5 h-1.5 bg-[#39FF14] rounded-full animate-pulse" />
                <span className="mono text-[10px] tracking-widest text-[#39FF14] uppercase">Per-user variance seed · no two subscribers ship the same portfolio</span>
              </div>
              <span className="mono text-[10px] tracking-widest text-zinc-500 uppercase">DK-ready CSV in one click</span>
            </div>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ["Vig-free", "Sharp-consensus props → DK points, every player"],
            ["Live", "Ownership forecast that sums like DK's %-drafted"],
            ["4×", "Candidates built, simmed and ranked on real payout curves"],
            ["Weekly", "Projection + ownership error graded vs DK results"],
          ].map(([n, l]) => (
            <div key={l} className="bg-[#121212] border border-zinc-800 p-4">
              <div className="mono text-2xl font-black text-[#39FF14]">{n}</div>
              <div className="overline mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const SYSTEMS = [
  { icon: Target, name: "Prop-market projections", desc: "Every projection is priced from player prop markets — passing, rushing, receiving yards, receptions, touchdowns — taken as a sharp-book consensus with the vig stripped and converted to DraftKings scoring. Rookies and new signings get a salary + depth-chart prior until their markets open. Your own edits always win." },
  { icon: Shield, name: "Live ownership from prop juice", desc: "A discrete-choice model of what the field sees: value, projection, implied totals, prop volume and where the public is juicing a line. Position totals sum the way DraftKings' real %-drafted does — 600% in Showdown, kickers included — and temperatures re-fit from real standings every week." },
  { icon: ChartLineUp, name: "Over-build, simulate, select", desc: "The engine builds up to 4× the lineups you asked for, runs every candidate through correlated game scripts against a sampled field, prices each one on the contest's actual DraftKings payout curve, and keeps the portfolio that pays — with a field-size-aware exposure taper so a 175k-entry Milly doesn't ride eight players at the cap." },
  { icon: Crosshair, name: "Rules that scale to your contest", desc: "Field size and max entries drive a continuous ruleset — stack mix, naked-QB share, ownership window, salary bands, min-unique, exposure caps, max offense against your own DST — interpolated between Milly Maker, large GPP, small GPP, 3-max, single entry and every cash sub-type. The Active Ruleset panel shows exactly what fired." },
  { icon: Cpu, name: "Showdown engine", desc: "A real Captain-mode solver: CPT + 5 FLEX with script mixes, leverage captains, per-player CPT min and max shares, kicker and DST ownership, and Millionaire-curve pricing for the big Showdown contests. The Captain Share chart shows who wore the crown across your entries." },
  { icon: FingerprintIcon, name: "Fingerprint Variance", desc: "Every subscriber's builds start from their own randomized seed, applied as a small correlated jitter to projections. Same slate, same settings, different portfolio — no two WarPaint users submit the same lineups." },
  { icon: Shuffle, name: "Cluster Rotation", desc: "A 14.2 and a 13.9 are the same player. After the solve, projection-equivalent peers rotate in to break shared cores without giving back expected value — never past your exposure caps, never into a player facing your own DST." },
  { icon: ListChecks, name: "Shadow Field · the second edge", desc: "Load the projection and ownership exports from the sites the crowd subscribes to. WarPaint rebuilds what their optimizers hand their users, rejects any lineup sharing 7 of 9 players with them, and caps your duplicate probability — an optional layer on top of numbers you already trust." },
  { icon: Warning, name: "Late Swap Alerts", desc: "The injury feed is polled while you're in the app. Anyone ruled out lands in a red alert listing every affected lineup, with one-tap Fix All that re-solves around your locked players — Smart or Manual — plus an email so you catch it away from the screen. Disagree with the feed? Restore any player and set your own number." },
  { icon: Scales, name: "Graded against real results", desc: "A pre-lock snapshot freezes every projection and ownership number. After the games, nflverse actuals and DK standings grade projection error by position, ownership error and chalk misses, which competitor site was sharpest, and how your portfolio actually finished." },
  { icon: Target, name: "Manual optimizer, your rules", desc: "A clean rules bar — lineups, salary window, exposure, min unique, QB stack, per-team max — with Fingerprint, Rotation and Shadow Field toggles and an Advanced drawer for group rules, presets and floors. Team Stack Heatmap and Cross-Contest Simulator read out what you built." },
  { icon: Cpu, name: "AI slate brief", desc: "A Claude Sonnet 5 strategist reads the slate — Vegas, implied totals, our projections versus the market, where ownership will pile up — and writes a plain-English attack plan for the week." },
];

function Systems() {
  return (
    <section id="systems" className="border-t border-zinc-900 py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <div className="overline mb-3">The engine</div>
          <h2 className="heading text-4xl md:text-5xl font-black uppercase tracking-tighter mb-4">
            Razor-sharp inputs.<br />Contest-shaped builds.
          </h2>
          <p className="text-lg text-zinc-300 max-w-2xl">
            Twelve systems, one order of operations: get the projections and ownership right, build for the exact
            contest, then put your entries where the crowd's optimizers structurally won't.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SYSTEMS.map(({ icon: Icon, name, desc }) => (
            <div key={name} className="bg-[#121212] border border-zinc-800 p-6 hover:border-[#39FF14] hover:shadow-[0_0_24px_-8px_#39FF14] transition-all group">
              <div className="w-10 h-10 flex items-center justify-center border border-[#39FF14]/40 bg-[#39FF14]/10 text-[#39FF14] mb-4 group-hover:bg-[#39FF14] group-hover:text-black transition-colors">
                <Icon size={20} weight="fill" />
              </div>
              <div className="heading font-bold text-white mb-2">{name}</div>
              <p className="text-sm text-zinc-300 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Proof() {
  return (
    <section id="proof" className="border-t border-zinc-900 py-24 bg-[#080808]">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="overline mb-3 text-[#39FF14]">Proof, not promises</div>
          <h2 className="heading text-4xl md:text-5xl font-black uppercase tracking-tighter mb-4">
            The engine grades itself<br />every week.
          </h2>
          <p className="text-base text-zinc-300 leading-relaxed mb-4">
            Most tools never tell you how wrong they were. WarPaint freezes its numbers before lock, then scores
            them against the real DraftKings standings: projection error by position, ownership error and chalk
            misses, which competitor site was sharpest, and how your best lineup actually finished.
          </p>
          <p className="text-base text-zinc-400 leading-relaxed">
            Those errors feed back into the model — ownership temperatures, per-position volatility and the trust
            given to each competitor site all move with the evidence. The scoreboard lives on your Portfolio page.
          </p>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["Pre-lock snapshot", "Every projection, ownership and volatility number frozen automatically before kickoff — grading never peeks at the post-game pool."],
            ["Projection accuracy", "Mean error and bias by position, ours versus the market consensus, worst misses and best calls."],
            ["Ownership accuracy", "Predicted versus actual %-drafted by position, with the chalk the field over- or under-rostered."],
            ["Site trust weights", "Each competitor site's error is tracked; the field model leans on whichever site has been sharpest."],
          ].map(([t, b]) => (
            <div key={t} className="bg-[#121212] border border-zinc-800 p-5">
              <div className="heading font-bold text-white mb-1.5">{t}</div>
              <p className="text-sm text-zinc-300 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const DK_ROWS = [
  { dim: "Roster + scoring", generic: "One projection set stretched across several sites with different flex rules, PPR variants and salary caps.", warpaint: "DraftKings only: the 9-man roster, full-PPR with 100-yard and 300-yard bonuses, and the $50k cap are hard constraints in every solve." },
  { dim: "Contest rules", generic: "The same max-entry output trimmed to fewer lineups. A 3-max, a 35k flea flicker and a 200k Milly Maker get the same construction.", warpaint: "Rules derive from the contest you enter: field size and entry limit set stack shapes, ownership windows, salary bands, exposure caps and the duplicate cap." },
  { dim: "Ownership", generic: "Ownership averaged from wherever it's posted first — noisy, and position totals don't add up.", warpaint: "A share model that sums the way DraftKings' actual %-drafted does, re-fit from real standings weekly, with the competitor consensus as the pessimistic ceiling." },
  { dim: "Duplicates", generic: "A vague 'uniqueness' slider.", warpaint: "An explicit cap on expected copies for your field size, plus a shadow field built from the sites the crowd uses so your lineups stay clear of theirs." },
  { dim: "Ranking", generic: "Lineups sorted by projected points.", warpaint: "Lineups ranked by correlated-simulation Field-Win %: how often they beat the field's bar, divided by the copies they'd split with." },
  { dim: "Late swap", generic: "Manual edits with no view of what's locked.", warpaint: "Lock-aware re-solve of open slots, scratches forced out, unique versus the rest of your portfolio, for Smart, Manual or DK-CSV portfolios." },
  { dim: "Accountability", generic: "Nothing. Last week's misses disappear.", warpaint: "Weekly grading against DraftKings standings, published on your Portfolio page." },
];

function DkNative() {
  return (
    <section id="dk-native" className="border-t border-zinc-900 py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <div className="overline mb-3 flex items-center gap-2 flex-wrap">
            <Trophy size={14} weight="fill" className="text-[#39FF14]" /> Why DraftKings-only
          </div>
          <h2 className="heading text-4xl md:text-5xl font-black uppercase tracking-tighter mb-6">
            One site. One ruleset.<br /><span className="text-[#39FF14]">Zero diluted math.</span>
          </h2>
          <p className="text-base text-zinc-300 leading-relaxed max-w-3xl">
            Multi-site tools average stack rules, blend scoring and smooth payout curves so one engine can serve six
            sites. WarPaint is written against DraftKings alone — the roster, the bonuses, the Milly Maker prize cliff —
            and carries separate rulebooks for cash, small-field, large-field and Showdown without compromising any of them.
          </p>
        </div>
        <div className="bg-[#121212] border border-zinc-800 overflow-x-auto mb-8">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-black">
              <tr className="text-left text-zinc-300 uppercase tracking-widest text-[10px]">
                <th className="px-4 py-3 w-40">Dimension</th>
                <th className="px-4 py-3"><div className="flex items-center gap-2"><X size={12} weight="bold" className="text-zinc-600" /> Multi-site optimizers</div></th>
                <th className="px-4 py-3"><div className="flex items-center gap-2 text-[#39FF14]"><Check size={12} weight="bold" /> WarPaint</div></th>
              </tr>
            </thead>
            <tbody>
              {DK_ROWS.map((r) => (
                <tr key={r.dim} className="border-t border-zinc-900 align-top">
                  <td className="px-4 py-4 text-zinc-300 heading font-bold uppercase text-xs tracking-wider">{r.dim}</td>
                  <td className="px-4 py-4 text-zinc-500 leading-relaxed">{r.generic}</td>
                  <td className="px-4 py-4 text-zinc-100 leading-relaxed border-l border-[#39FF14]/20">{r.warpaint}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            ["text-[#39FF14]", "Large-field", "Milly Maker · 20-Max", "Duplicate cap, competitor shadow field, leverage anchors and contest-shaped stacks tuned for six-figure fields where copies kill your payout."],
            ["text-[#FFCC00]", "Mid & small-field", "Flea Flicker · 3-Max · Single-Entry", "Tighter correlation, higher floors, relaxed ownership windows and concentrated exposure when you only get a few bullets."],
            ["text-[#E11D2E]", "Showdown", "Captain mode", "Game-script routing from the spread and total, captain exposure budgets, K/DST captain limits and bring-back logic for one-game slates."],
            ["text-white", "Cash", "50/50 · Double-up · H2H", "Floor objective on median outcomes — no leverage forcing, no ceiling chasing, the highest-floor legal lineup."],
          ].map(([c, o, t, b]) => (
            <div key={t} className="bg-[#121212] border border-zinc-800 p-6 flex flex-col">
              <div className={`overline mb-2 ${c}`}>{o}</div>
              <div className="heading font-bold mb-3 text-base min-h-[3rem]">{t}</div>
              <p className="text-sm text-zinc-300 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const COMPARISON = [
  { feature: "Projections priced from a vig-stripped sharp-book consensus of player props, converted to DK scoring", us: true, a: "partial", b: "partial", c: false },
  { feature: "Live ownership forecast driven by prop juice — sums like real %-drafted (600% in Showdown), re-fit weekly", us: true, a: false, b: false, c: false },
  { feature: "Over-build 4× → correlated sim → selection on the contest's actual DK payout curve", us: true, a: false, b: "partial", c: false },
  { feature: "Field-size-aware exposure taper (no eight-player spine at the cap in a 175k field)", us: true, a: false, b: false, c: false },
  { feature: "Showdown: CPT min/max per player, leverage captains, K + DST ownership, Millionaire-curve pricing", us: true, a: "partial", b: "partial", c: false },
  { feature: "Shadow Field: rebuild the commercial optimizers' lineups from their exports and step around them (optional)", us: true, a: false, b: false, c: false },
  { feature: "Explicit expected-copies cap for your field size", us: true, a: false, b: "partial", c: false },
  { feature: "Correlated Monte Carlo (game → team → player) with Field-Win % ranking", us: true, a: "partial", b: true, c: false },
  { feature: "Contest rules derived from field size + entry limit (Milly / 20-max / 3-max / SE / Showdown / cash)", us: true, a: "partial", b: "partial", c: false },
  { feature: "Vegas game-script stack routing per lineup", us: true, a: false, b: false, c: false },
  { feature: "Per-user variance seed — no two subscribers ship the same portfolio", us: true, a: false, b: false, c: false },
  { feature: "Post-solve peer swap for uniqueness without EV loss", us: true, a: false, b: false, c: false },
  { feature: "Late Swap Alerts: polled injury feed, red banner per lineup, one-tap Fix All + email, per-player restore override", us: true, a: false, b: "partial", c: false },
  { feature: "Lock-aware Late Swap re-solve (open slots re-optimized, locked players kept)", us: true, a: "partial", b: true, c: false },
  { feature: "Weekly self-grading against DraftKings standings (projection + ownership error)", us: true, a: false, b: false, c: false },
  { feature: "Competitor site trust weights learned from graded results", us: true, a: false, b: false, c: false },
  { feature: "Pre-lock preflight checklist (feeds, injuries, ownership sanity, coverage)", us: true, a: false, b: false, c: false },
  { feature: "Manual optimizer: clean rules bar + Fingerprint / Rotation / Shadow Field toggles, group rules, Team Stack Heatmap", us: true, a: true, b: true, c: true },
  { feature: "Every DK slate synced from the lobby — Thu, Early Only, Afternoon Only, Sun-Mon, every Showdown", us: true, a: true, b: true, c: "partial" },
  { feature: "Row-level projection & ownership overrides", us: true, a: true, b: true, c: true },
  { feature: "Bankroll-aware entry sizing on the Build screen", us: true, a: false, b: false, c: false },
  { feature: "AI slate brief (Claude Sonnet 5)", us: true, a: false, b: false, c: false },
  { feature: "Live Vegas · prop-market projections · injuries · depth charts · weather included", us: true, a: true, b: true, c: "partial" },
  { feature: "Price", us: "$99/week · everything", a: "$97–$299/mo by tier", b: "$110–$349/mo + sim credits", c: "$29–$149/mo bundle" },
  { feature: "Commitment", us: "Weekly · cancel any week", a: "Monthly / annual", b: "Monthly + credit packs", c: "Monthly / annual" },
  { feature: "Pricing model", us: "One plan, every feature", a: "Feature-gated tiers", b: "Token-gated simulations", c: "Multi-sport content bundle" },
  { feature: "DraftKings-native rules, scoring and payout curves", us: "Purpose-built", a: "Multi-site generic", b: "Multi-site generic", c: "Multi-site generic" },
];

function Comparison() {
  return (
    <section id="comparison" className="border-t border-zinc-900 py-24 bg-[#080808]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <div className="overline mb-3">Head to head</div>
          <h2 className="heading text-4xl md:text-5xl font-black uppercase tracking-tighter mb-4">
            Stop playing against the players.<br />Start playing against their optimizers.
          </h2>
          <p className="text-base text-zinc-300 leading-relaxed max-w-3xl">
            The tools below are good at what they do — which is exactly the problem. Tens of thousands of entries
            are built from the same three or four projection sets every week. WarPaint doesn't try to out-project
            them; it ingests their numbers and builds where they aren't. One tier,
            <span className="text-white font-semibold"> every feature included</span>.
          </p>
        </div>
        <div className="mb-6">
          <div className="overline text-[#39FF14] mb-2">The 5-second read</div>
          <div className="bg-[#121212] border-2 border-[#39FF14]/40 overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-black">
                <tr className="text-left text-zinc-200 uppercase tracking-widest text-[10px]">
                  <th className="px-4 py-3.5 w-52">What it optimizes</th>
                  <th className="px-4 py-3.5 text-center text-[#39FF14] w-[19%]">WarPaint DFS</th>
                  <th className="px-4 py-3.5 text-center w-[19%]">Traditional Pro-Tier Sites</th>
                  <th className="px-4 py-3.5 text-center w-[19%]">Token-Gated Simulators</th>
                  <th className="px-4 py-3.5 text-center w-[19%]">Mass-Market Content Tiers</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { f: "Objective", us: "Beat the field's lineups", a: "Maximize projected points", b: "Maximize simulated points", c: "Maximize projected points" },
                  { f: "Field model", us: "Their consensus, ingested", a: "None", b: "Ownership-weighted random", c: "None" },
                  { f: "Duplicates", us: "Explicit copies cap", a: "Uniqueness slider", b: "Estimated", c: "None" },
                  { f: "Accountability", us: "Graded vs DK weekly", a: "None", b: "None", c: "None" },
                  { f: "Price", us: "$99/week · all-in", a: "$150–$299/mo · gated", b: "$200–$350/mo · credits", c: "$29–$149/mo · bundle" },
                ].map((r) => (
                  <tr key={r.f} className="border-t border-zinc-900 align-middle">
                    <td className="px-4 py-4 text-white font-semibold text-[13px]">{r.f}</td>
                    <td className="px-4 py-4 text-center text-[#39FF14] font-bold text-[13px]">{r.us}</td>
                    <td className="px-4 py-4 text-center text-zinc-400 text-[12px]">{r.a}</td>
                    <td className="px-4 py-4 text-center text-zinc-400 text-[12px]">{r.b}</td>
                    <td className="px-4 py-4 text-center text-zinc-400 text-[12px]">{r.c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mt-2">Line-by-line below ↓</div>
        </div>
        <div className="bg-[#121212] border border-zinc-800 overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-black">
              <tr className="text-left text-zinc-300 uppercase tracking-widest text-[10px]">
                <th className="px-4 py-3 w-56">Feature</th>
                <th className="px-4 py-3 text-center text-[#39FF14] align-top w-[19%]">WarPaint DFS</th>
                <th className="px-4 py-3 text-center align-top w-[19%]">Traditional Pro-Tier Sites<br /><span className="mono text-[9px] text-zinc-500 normal-case tracking-normal">projection-first subscriptions</span></th>
                <th className="px-4 py-3 text-center align-top w-[19%]">Token-Gated Simulators<br /><span className="mono text-[9px] text-zinc-500 normal-case tracking-normal">pay-per-simulation tools</span></th>
                <th className="px-4 py-3 text-center align-top w-[19%]">Mass-Market Content Tiers<br /><span className="mono text-[9px] text-zinc-500 normal-case tracking-normal">multi-sport bundles</span></th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((r) => (
                <tr key={r.feature} className="border-t border-zinc-900 align-top">
                  <td className="px-4 py-3 text-zinc-200">{r.feature}</td>
                  <td className="px-4 py-3 text-center align-top"><Cell v={r.us} highlight /></td>
                  <td className="px-4 py-3 text-center align-top"><Cell v={r.a} /></td>
                  <td className="px-4 py-3 text-center align-top"><Cell v={r.b} /></td>
                  <td className="px-4 py-3 text-center align-top"><Cell v={r.c} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-3">
          Category descriptions reflect publicly advertised plans as of the 2026 preseason. Prices vary by promotion.
        </p>
      </div>
    </section>
  );
}

function Cell({ v, highlight }) {
  if (v === true) return <span className={highlight ? "text-[#39FF14] font-bold" : "text-zinc-300"}>✓</span>;
  if (v === false) return <span className="text-zinc-700">—</span>;
  if (v === "partial") return <span className="text-[#FFCC00] text-xs mono">PARTIAL</span>;
  return <span className={`mono text-[10px] leading-snug block ${highlight ? "text-[#39FF14] font-bold" : "text-zinc-300"}`}>{v}</span>;
}

function Pricing({ onPick }) {
  return (
    <section id="pricing" className="border-t border-zinc-900 py-24">
      <div className="max-w-3xl mx-auto px-6">
        <div className="mb-12 text-center">
          <div className="overline mb-3">Pricing</div>
          <h2 className="heading text-4xl md:text-5xl font-black uppercase tracking-tighter mb-4">$99 a week. Every feature.<br />Cancel any week.</h2>
          <p className="text-base text-zinc-300 leading-relaxed max-w-2xl mx-auto">
            DFS is played week to week, so that's how we bill. No tiers, no add-ons, no season-long commitment —
            pay for the Sundays you play and keep every feature from the first build.
          </p>
        </div>
        <div className="bg-gradient-to-b from-[#121212] to-[#0a1a0a] border-2 border-[#39FF14] shadow-[0_0_48px_-12px_#39FF14] p-8 relative" data-testid="landing-plan-weekly">
          <div className="absolute -top-3 left-6 bg-[#39FF14] text-black px-3 py-1 text-[10px] uppercase tracking-widest font-black shadow-[0_0_16px_-4px_#39FF14]">All-Access</div>
          <div className="overline mb-3 text-[#39FF14]">Weekly</div>
          <div className="heading text-6xl lg:text-7xl font-black text-white mb-2 leading-none">$99<span className="text-xl text-zinc-500">/week</span></div>
          <div className="text-sm text-zinc-300 mb-6">Renews every 7 days. Cancel from your account and keep access through the week you paid for.</div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm text-zinc-300 mb-6">
            <li>✓ Smart Optimizer with contest-shaped rules</li>
            <li>✓ Field model from the sites the crowd uses</li>
            <li>✓ Correlated Monte Carlo + Field-Win % ranking</li>
            <li>✓ Manual Optimizer with group rules and peer swap</li>
            <li>✓ Pre-lock scratch fix + lock-aware Late Swap</li>
            <li>✓ Weekly self-grading against DK standings</li>
            <li>✓ AI slate brief</li>
            <li>✓ Unlimited slates · DK-ready CSV</li>
          </ul>
          <button onClick={onPick} data-testid="pick-weekly" className="w-full py-3.5 bg-[#39FF14] text-black hover:bg-[#2ee30d] uppercase tracking-widest text-sm font-black shadow-[0_0_24px_-6px_#39FF14] hover:shadow-[0_0_40px_-4px_#39FF14] transition-shadow">Start This Week — $99</button>
        </div>
      </div>
    </section>
  );
}

function NewToDFS() {
  const [open, setOpen] = React.useState(false);
  const FAQ = [
    { q: "What is DFS and how does DraftKings work?", a: "Daily Fantasy Sports lets you build a 9-player NFL roster under a $50,000 salary cap on DraftKings. Real Sunday stats score your lineup and the top finishers cash. Contests pay very differently: the Milly Maker pays $1M to one lineup out of ~200,000, while a 50/50 only needs you to beat the median." },
    { q: "What does the Shadow Field actually do?", a: "Most of the field builds lineups with a handful of commercial optimizers. If you load those sites' projection and ownership exports into WarPaint, the engine treats their consensus as the field: it estimates which lineups they'll produce, how many copies each will have, and keeps your entries clear of them. Your own projections still drive your builds." },
    { q: "Do I need subscriptions to other sites for this to work?", a: "No. Without competitor exports the engine builds against its own estimate of the field, using market projections and our ownership model. Loading one or more sites sharpens the field model; the Build screen shows how much of the player pool that consensus covers." },
    { q: "How many lineups should I enter?", a: "Pick the contest and your entry count and the rules scale to it — 1 for single-entry, 3 for 3-max, 20 for 20-max, up to 150 for the Milly Maker. The bankroll strip suggests a stake-appropriate count if you tell it your bankroll." },
    { q: "Where do the projections and data come from?", a: "Player prop markets from a sharp-book consensus feed (vig-stripped and converted to DraftKings scoring), Vegas lines and implied totals, Sleeper injuries and depth charts, Open-Meteo weather and nflverse defense and EPA data. Players with no market and no analyst number are excluded rather than invented." },
    { q: "How do I get lineups onto DraftKings?", a: "Download the CSV from your Portfolio, open DraftKings' bulk upload page, drop the file and click Import. Under 30 seconds, no browser extension." },
    { q: "How do I know it's working?", a: "Upload the DraftKings standings on Monday. The engine grades its pre-lock projections and ownership against what actually happened and publishes the scoreboard on your Portfolio page." },
    { q: "Is DFS legal in my state?", a: "DFS is regulated state by state. DraftKings only shows an Enter button on contests you're eligible for. WarPaint never handles wagers and has no state restrictions of its own." },
  ];
  return (
    <section id="new-to-dfs" className="border-t border-zinc-900 py-20 bg-[#080808]">
      <div className="max-w-4xl mx-auto px-6">
        <button onClick={() => setOpen((v) => !v)} data-testid="new-to-dfs-toggle"
          className="w-full flex items-center justify-between gap-4 border border-zinc-800 hover:border-[#39FF14]/60 bg-[#121212] px-6 py-5 transition-colors group">
          <div className="text-left">
            <div className="overline mb-1 text-[#39FF14]">Questions</div>
            <div className="heading text-xl md:text-2xl font-black uppercase tracking-tight">New here? Read this first.</div>
            <div className="text-sm text-zinc-300 mt-1">DraftKings basics, what the field model does, and how to know it's working.</div>
          </div>
          <div className={`text-[#39FF14] mono text-2xl transition-transform ${open ? "rotate-45" : ""}`}>+</div>
        </button>
        {open && (
          <div className="mt-4 space-y-3">
            {FAQ.map((item, i) => (
              <details key={i} className="bg-[#121212] border border-zinc-800 open:border-[#39FF14]/40">
                <summary className="cursor-pointer px-5 py-4 flex items-center justify-between gap-4 list-none">
                  <span className="text-white font-semibold text-sm">{item.q}</span>
                  <span className="text-[#39FF14] mono text-lg">›</span>
                </summary>
                <div className="px-5 pb-4 -mt-1 text-sm text-zinc-300 leading-relaxed border-t border-zinc-900 pt-3">{item.a}</div>
              </details>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-zinc-900 py-10 mt-8" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-6 space-y-6">
        <div className="border border-zinc-800 bg-black/40 px-4 py-3 text-[11px] mono text-zinc-500 leading-relaxed">
          WarPaint DFS is an independent analytics platform. WarPaint DFS is
          <strong className="text-zinc-300"> not affiliated with, endorsed by, sponsored by, or connected to DraftKings, Inc.</strong>,
          the National Football League, any team, coach, or player, or any third-party projection or DFS analytics provider. WarPaint does not source, sell or redistribute third-party projections; any files you upload are processed for your account only. Daily fantasy sports contests involve substantial risk of
          financial loss. You must be at least 18 years old (21+ in MA/IA/LA) to use paid features. Not available in AZ, HI, ID, LA, MT, NV, WA.
          If gambling is causing you harm, call <a href="tel:1-800-426-2537" className="text-[#39FF14]">1-800-GAMBLER</a>.
        </div>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-zinc-500 mono">
          <div>© {new Date().getFullYear()} WarPaint DFS · Built to beat the field</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 uppercase tracking-widest text-[10px]">
            <a href="#systems" className="hover:text-white">Engine</a>
            <a href="#pricing" className="hover:text-white">Pricing</a>
            <a href="/terms" className="hover:text-white" data-testid="footer-terms">Terms</a>
            <a href="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy</a>
            <a href="/refunds" className="hover:text-white" data-testid="footer-refunds">Refunds</a>
            <a href="/responsible-gambling" className="hover:text-white text-[#FFCC00]" data-testid="footer-responsible-gambling">Play Responsibly</a>
            <a href="mailto:support@warpaintdfs.com" className="hover:text-white">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
